import {
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { mockApi } from "../api/mockApi";

// Import AG Grid Enterprise and register modules
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  AllEnterpriseModule,
  IntegratedChartsModule
} from "ag-grid-enterprise";
import CustomerDetailModel from "../components/CustomerDetailModel";

// Register AG Grid modules including charts
ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

// Mock customer data
const mockCustomers = [
  {
    step: 11,
    stage: "CONGRATULATIONS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_700",
    emailAddress: "s.hassan.92@yahoo.com",
    firstName: "Samira",
    lastName: "Hassan",
    homeType: ["DETACHED"],
    currentStage: "JUST_GETTING_STARTED",
    phoneNumber: "+14162834719",
    referralSource: "FACEBOOK",
    timeframe: "THREE_TO_SIX_MONTHS",
    totalIncome: 9000000,
    totalSavings: 5000000,
    affordability: 45500000,
    zownContribution: 682500,
    qualificationStatus: "QUALIFIED",
    zid: "951dccd4-c4d1-423a-91aa-d968c34f4a42",
    zvid: "250125042911",
    vid: "250125042911",
    zcid: "250320170841834",
    cid: "250320170841834",
    bid: "97934089-5278-4127-8cdd-a7f84c068e5a",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 8,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "650_TO_700",
    emailAddress: "mthompson47@gmail.com",
    firstName: "Marcus",
    lastName: "Thompson",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+16479416823",
    referralSource: "GOOGLE",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 7500000,
    totalSavings: 3000000,
    affordability: 35000000,
    zownContribution: 525000,
    qualificationStatus: "PENDING",
    zid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    zvid: "250125042912",
    vid: "250125042912",
    zcid: "250320170841835",
    cid: "250320170841835",
    bid: "12345678-9abc-def0-1234-56789abcdef0",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: true
  },
  {
    step: 15,
    stage: "COMPLETED",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_750",
    emailAddress: "bella_martinez88@outlook.com",
    firstName: "Isabella",
    lastName: "Martinez",
    homeType: ["TOWNHOUSE"],
    currentStage: "READY_TO_BUY",
    phoneNumber: "+14167291468",
    referralSource: "INSTAGRAM",
    timeframe: "IMMEDIATELY",
    totalIncome: 12000000,
    totalSavings: 8000000,
    affordability: 65000000,
    zownContribution: 975000,
    qualificationStatus: "QUALIFIED",
    zid: "xyz789-abc-def1-2345-6789abcdef12",
    zvid: "250125042913",
    vid: "250125042913",
    zcid: "250320170841836",
    cid: "250320170841836",
    bid: "fedcba98-7654-3210-fedc-ba9876543210",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 5,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "600_TO_650",
    emailAddress: "nwong2024@hotmail.com",
    firstName: "Nathan",
    lastName: "Wong",
    homeType: ["DETACHED"],
    currentStage: "RESEARCHING",
    phoneNumber: "+16478312647",
    referralSource: "GOOGLE",
    timeframe: "SIX_MONTHS_TO_ONE_YEAR",
    totalIncome: 6000000,
    totalSavings: 2500000,
    affordability: 30000000,
    zownContribution: 450000,
    qualificationStatus: "PENDING",
    zid: "michael-123-456-789",
    zvid: "250125042914",
    vid: "250125042914",
    zcid: "250320170841837",
    cid: "250320170841837",
    bid: "michael-bid-123-456",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 12,
    stage: "CONGRATULATIONS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_750",
    emailAddress: "priyasharma.home@gmail.com",
    firstName: "Priya",
    lastName: "Sharma",
    homeType: ["CONDO"],
    currentStage: "READY_TO_BUY",
    phoneNumber: "+14163928471",
    referralSource: "FACEBOOK",
    timeframe: "IMMEDIATELY",
    totalIncome: 11000000,
    totalSavings: 7000000,
    affordability: 55000000,
    zownContribution: 825000,
    qualificationStatus: "QUALIFIED",
    zid: "emma-789-012-345",
    zvid: "250125042915",
    vid: "250125042915",
    zcid: "250320170841838",
    cid: "250320170841838",
    bid: "emma-bid-789-012",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 3,
    stage: "STARTED",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "BELOW_600",
    emailAddress: "t_anderson_23@yahoo.com",
    firstName: "Taylor",
    lastName: "Anderson",
    homeType: ["TOWNHOUSE"],
    currentStage: "EXPLORING_OPTIONS",
    phoneNumber: "+16472918364",
    referralSource: "INSTAGRAM",
    timeframe: "MORE_THAN_ONE_YEAR",
    totalIncome: 4500000,
    totalSavings: 1500000,
    affordability: 22000000,
    zownContribution: 330000,
    qualificationStatus: "NOT_QUALIFIED",
    zid: "alex-345-678-901",
    zvid: "250125042916",
    vid: "250125042916",
    zcid: "250320170841839",
    cid: "250320170841839",
    bid: "alex-bid-345-678",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: true
  },
  {
    step: 9,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "700_TO_750",
    emailAddress: "oliviabennett.realty@outlook.com",
    firstName: "Olivia",
    lastName: "Bennett",
    homeType: ["DETACHED"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+14168473192",
    referralSource: "GOOGLE",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 8500000,
    totalSavings: 4500000,
    affordability: 42000000,
    zownContribution: 630000,
    qualificationStatus: "QUALIFIED",
    zid: "lisa-567-890-123",
    zvid: "250125042917",
    vid: "250125042917",
    zcid: "250320170841840",
    cid: "250320170841840",
    bid: "lisa-bid-567-890",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 7,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_700",
    emailAddress: "jamesbourikos@gmail.com",
    firstName: "James",
    lastName: "Bourikos",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+14167057733",
    referralSource: "GOOGLE",
    timeframe: "THREE_TO_SIX_MONTHS",
    totalIncome: 8200000,
    totalSavings: 3800000,
    affordability: 38000000,
    zownContribution: 570000,
    qualificationStatus: "QUALIFIED",
    zid: "james-bid-001-234",
    zvid: "250125042918",
    vid: "250125042918",
    zcid: "250320170841841",
    cid: "250320170841841",
    bid: "james-bid-001-234",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 4,
    stage: "STARTED",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "650_TO_700",
    emailAddress: "jdpokeadot@gmail.com",
    firstName: "Hahhs",
    lastName: "Bsbsh",
    homeType: ["TOWNHOUSE"],
    currentStage: "EXPLORING_OPTIONS",
    phoneNumber: "+12896837333",
    referralSource: "GOOGLE",
    timeframe: "SIX_MONTHS_TO_ONE_YEAR",
    totalIncome: 5800000,
    totalSavings: 2200000,
    affordability: 28000000,
    zownContribution: 420000,
    qualificationStatus: "PENDING",
    zid: "hahhs-bid-002-345",
    zvid: "250125042919",
    vid: "250125042919",
    zcid: "250320170841842",
    cid: "250320170841842",
    bid: "hahhs-bid-002-345",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 10,
    stage: "CONGRATULATIONS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_750",
    emailAddress: "brunobragasurf@gmail.com",
    firstName: "Bruno",
    lastName: "Braga",
    homeType: ["DETACHED"],
    currentStage: "READY_TO_BUY",
    phoneNumber: "+16477172600",
    referralSource: "GOOGLE",
    timeframe: "IMMEDIATELY",
    totalIncome: 10500000,
    totalSavings: 6500000,
    affordability: 52000000,
    zownContribution: 780000,
    qualificationStatus: "QUALIFIED",
    zid: "bruno-bid-003-456",
    zvid: "250125042920",
    vid: "250125042920",
    zcid: "250320170841843",
    cid: "250320170841843",
    bid: "bruno-bid-003-456",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 6,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "700_TO_750",
    emailAddress: "kenechieze2@gmail.com",
    firstName: "Kenechi",
    lastName: "Eze",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+12269297076",
    referralSource: "FACEBOOK",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 7800000,
    totalSavings: 3500000,
    affordability: 37000000,
    zownContribution: 555000,
    qualificationStatus: "QUALIFIED",
    zid: "kenechi-bid-004-567",
    zvid: "250125042921",
    vid: "250125042921",
    zcid: "250320170841844",
    cid: "250320170841844",
    bid: "kenechi-bid-004-567",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 5,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "650_TO_700",
    emailAddress: "john123455551234@gmail.com",
    firstName: "John",
    lastName: "Smith",
    homeType: ["TOWNHOUSE"],
    currentStage: "RESEARCHING",
    phoneNumber: "+16475258712",
    referralSource: "GOOGLE",
    timeframe: "THREE_TO_SIX_MONTHS",
    totalIncome: 6500000,
    totalSavings: 2800000,
    affordability: 31000000,
    zownContribution: 465000,
    qualificationStatus: "PENDING",
    zid: "john-bid-005-678",
    zvid: "250125042922",
    vid: "250125042922",
    zcid: "250320170841845",
    cid: "250320170841845",
    bid: "john-bid-005-678",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 11,
    stage: "CONGRATULATIONS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_750",
    emailAddress: "zac.weinberger@outlook.com",
    firstName: "Zac",
    lastName: "Weinberger",
    homeType: ["DETACHED"],
    currentStage: "READY_TO_BUY",
    phoneNumber: "+12263770615",
    referralSource: "FACEBOOK",
    timeframe: "IMMEDIATELY",
    totalIncome: 11500000,
    totalSavings: 7500000,
    affordability: 58000000,
    zownContribution: 870000,
    qualificationStatus: "QUALIFIED",
    zid: "zac-bid-006-789",
    zvid: "250125042923",
    vid: "250125042923",
    zcid: "250320170841846",
    cid: "250320170841846",
    bid: "zac-bid-006-789",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 6,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "700_TO_750",
    emailAddress: "drumisales@gmail.com",
    firstName: "Andrew",
    lastName: "Drumm",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+14165531314",
    referralSource: "FACEBOOK",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 7200000,
    totalSavings: 3200000,
    affordability: 35000000,
    zownContribution: 525000,
    qualificationStatus: "PENDING",
    zid: "andrew-bid-007-890",
    zvid: "250125042924",
    vid: "250125042924",
    zcid: "250320170841847",
    cid: "250320170841847",
    bid: "andrew-bid-007-890",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 12,
    stage: "CONGRATULATIONS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_750",
    emailAddress: "karli.ttyll@gmail.com",
    firstName: "Carli",
    lastName: "Reardon",
    homeType: ["TOWNHOUSE"],
    currentStage: "READY_TO_BUY",
    phoneNumber: "+16138598073",
    referralSource: "INSTAGRAM",
    timeframe: "IMMEDIATELY",
    totalIncome: 10200000,
    totalSavings: 6200000,
    affordability: 51000000,
    zownContribution: 765000,
    qualificationStatus: "QUALIFIED",
    zid: "carli-bid-008-901",
    zvid: "250125042925",
    vid: "250125042925",
    zcid: "250320170841848",
    cid: "250320170841848",
    bid: "carli-bid-008-901",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 8,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_700",
    emailAddress: "slymoments@gmail.com",
    firstName: "Unysiah",
    lastName: "Morris",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+16477177292",
    referralSource: "FACEBOOK",
    timeframe: "THREE_TO_SIX_MONTHS",
    totalIncome: 8800000,
    totalSavings: 4200000,
    affordability: 42000000,
    zownContribution: 630000,
    qualificationStatus: "QUALIFIED",
    zid: "unysiah-bid-009-012",
    zvid: "250125042926",
    vid: "250125042926",
    zcid: "250320170841849",
    cid: "250320170841849",
    bid: "unysiah-bid-009-012",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 5,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "650_TO_700",
    emailAddress: "jaynishp@ymail.com",
    firstName: "Jaynish",
    lastName: "Patel",
    homeType: ["DETACHED"],
    currentStage: "RESEARCHING",
    phoneNumber: "+16475713314",
    referralSource: "GOOGLE",
    timeframe: "SIX_MONTHS_TO_ONE_YEAR",
    totalIncome: 6800000,
    totalSavings: 2900000,
    affordability: 32000000,
    zownContribution: 480000,
    qualificationStatus: "PENDING",
    zid: "jaynish-bid-010-123",
    zvid: "250125042927",
    vid: "250125042927",
    zcid: "250320170841850",
    cid: "250320170841850",
    bid: "jaynish-bid-010-123",
    sentForPreApproval: false,
    showedUp: false,
    assignedToAM: false,
    noShow: false
  },
  {
    step: 9,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "700_TO_750",
    emailAddress: "smaysantos@gmail.com",
    firstName: "Sunshine",
    lastName: "Santos",
    homeType: ["CONDO"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+16479789843",
    referralSource: "FACEBOOK",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 8000000,
    totalSavings: 3700000,
    affordability: 38000000,
    zownContribution: 570000,
    qualificationStatus: "QUALIFIED",
    zid: "sunshine-bid-011-234",
    zvid: "250125042928",
    vid: "250125042928",
    zcid: "250320170841851",
    cid: "250320170841851",
    bid: "sunshine-bid-011-234",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  },
  {
    step: 7,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "ABOVE_700",
    emailAddress: "burkie44@live.ca",
    firstName: "Callum",
    lastName: "Burke",
    homeType: ["TOWNHOUSE"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+16479751393",
    referralSource: "FACEBOOK",
    timeframe: "THREE_TO_SIX_MONTHS",
    totalIncome: 7600000,
    totalSavings: 3400000,
    affordability: 36000000,
    zownContribution: 540000,
    qualificationStatus: "QUALIFIED",
    zid: "callum-bid-012-345",
    zvid: "250125042929",
    vid: "250125042929",
    zcid: "250320170841852",
    cid: "250320170841852",
    bid: "callum-bid-012-345",
    sentForPreApproval: true,
    showedUp: false,
    assignedToAM: true,
    noShow: true
  },
  {
    step: 8,
    stage: "IN_PROGRESS",
    formId: "ZownCABuyerQualifyContactForm",
    creditScore: "700_TO_750",
    emailAddress: "thechappilly@gmail.com",
    firstName: "Thomas",
    lastName: "Paul",
    homeType: ["DETACHED"],
    currentStage: "ACTIVELY_SEARCHING",
    phoneNumber: "+16477049439",
    referralSource: "FACEBOOK",
    timeframe: "ONE_TO_THREE_MONTHS",
    totalIncome: 8400000,
    totalSavings: 4000000,
    affordability: 40000000,
    zownContribution: 600000,
    qualificationStatus: "QUALIFIED",
    zid: "thomas-bid-013-456",
    zvid: "250125042930",
    vid: "250125042930",
    zcid: "250320170841853",
    cid: "250320170841853",
    bid: "thomas-bid-013-456",
    sentForPreApproval: true,
    showedUp: true,
    assignedToAM: true,
    noShow: false
  }
];

function AdDetail() {
  const { campaignId, adsetId, adId } = useParams<{
    campaignId: string;
    adsetId: string;
    adId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  // Fix for Leaflet default markers in React
  useEffect(() => {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
    });
  }, []);

  // Function to navigate back with preserved filters
  const navigateBackToCampaigns = () => {
    const backUrl = `/campaigns${location.search}`;
    navigate(backUrl);
  };

  // Handle customer row click
  const handleCustomerRowClick = useCallback((customer: any) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  }, []);

  // Helper function to format money (cents to dollars)
  const formatMoney = (cents: number) => {
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "QUALIFIED":
        return "green";
      case "PENDING":
        return "yellow";
      case "NOT_QUALIFIED":
        return "red";
      default:
        return "gray";
    }
  };

  // Helper function to get stage color
  const getStageColor = (stage: string) => {
    switch (stage) {
      case "COMPLETED":
        return "green";
      case "CONGRATULATIONS":
        return "blue";
      case "IN_PROGRESS":
        return "orange";
      default:
        return "gray";
    }
  };

  // AG Grid column definitions for customers
  const customerColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Name",
        field: "firstName",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 150,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const customer = params.data;
          return (
            <Box cursor="pointer" _hover={{ bg: "blue.50" }} p={1} rounded="md">
              <Text fontWeight="medium" fontSize="sm">
                {customer.firstName} {customer.lastName}
              </Text>
              <Text fontSize="xs" color="gray.600">
                Step {customer.step}
              </Text>
            </Box>
          );
        }
      },
      {
        headerName: "Email",
        field: "emailAddress",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 180,
        maxWidth: 220,
        cellRenderer: (params: any) => {
          return (
            <Text fontSize="sm" color="blue.600">
              {params.value}
            </Text>
          );
        }
      },
      {
        headerName: "Phone",
        field: "phoneNumber",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          return (
            <Text fontSize="sm" color="gray.700">
              {params.value}
            </Text>
          );
        }
      },
      {
        headerName: "Status",
        field: "qualificationStatus",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 130,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          const customer = params.data;
          return (
            <VStack align="start" gap={1}>
              <Badge
                colorScheme={getStatusColor(customer.qualificationStatus)}
                size="sm"
              >
                {customer.qualificationStatus}
              </Badge>
              <Badge colorScheme={getStageColor(customer.stage)} size="sm">
                {customer.stage.replace(/_/g, " ")}
              </Badge>
            </VStack>
          );
        }
      },
      {
        headerName: "Credit Score",
        field: "creditScore",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          return (
            <Text fontSize="sm" fontWeight="medium">
              {params.value ? params.value.replace(/_/g, " ") : "N/A"}
            </Text>
          );
        }
      },
      {
        headerName: "Income",
        field: "totalIncome",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Text fontSize="sm" fontWeight="bold" color="green.600">
              {formatMoney(params.value)}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Savings",
        field: "totalSavings",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Text fontSize="sm" fontWeight="bold" color="blue.600">
              {formatMoney(params.value)}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Affordability",
        field: "affordability",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 130,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Text fontSize="sm" fontWeight="bold" color="purple.600">
              {formatMoney(params.value)}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Zown Contribution",
        field: "zownContribution",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 170,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Text fontSize="sm" fontWeight="bold" color="orange.500">
              {formatMoney(params.value)}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Home Type",
        field: "homeType",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          const homeTypes = params.value;
          return homeTypes && homeTypes.length > 0 ? (
            <Text fontSize="sm">{homeTypes.join(", ").replace(/_/g, " ")}</Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Timeframe",
        field: "timeframe",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 150,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Text fontSize="sm">{params.value.replace(/_/g, " ")}</Text>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Source",
        field: "referralSource",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Badge variant="outline" size="sm">
              {params.value}
            </Badge>
          ) : (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              N/A
            </Text>
          );
        }
      },
      {
        headerName: "Sent for Pre-Approval",
        field: "sentForPreApproval",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 160,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Badge colorScheme="orange" size="sm">
              Yes
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="outline" size="sm">
              No
            </Badge>
          );
        }
      },
      {
        headerName: "Showed Up",
        field: "showedUp",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 110,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Badge colorScheme="blue" size="sm">
              Yes
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="outline" size="sm">
              No
            </Badge>
          );
        }
      },
      {
        headerName: "Assigned to AM",
        field: "assignedToAM",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 130,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Badge colorScheme="green" size="sm">
              Yes
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="outline" size="sm">
              No
            </Badge>
          );
        }
      },
      {
        headerName: "No-Show",
        field: "noShow",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: (params: any) => {
          return params.value ? (
            <Badge colorScheme="red" size="sm">
              Yes
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="outline" size="sm">
              No
            </Badge>
          );
        }
      }
    ],
    [formatMoney, getStatusColor, getStageColor]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      suppressSizeToFit: true,
      autoHeaderHeight: true
    }),
    []
  );

  // Grid ready event
  const onGridReady = useCallback((event: GridReadyEvent) => {
    // Auto-size all columns based on content
    event.api.autoSizeAllColumns();
  }, []);

  // Fetch campaign data based on campaign ID
  const {
    data: campaign,
    isLoading,
    error
  } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () => mockApi.getCampaign(campaignId!),
    enabled: !!campaignId
  });

  if (isLoading) {
    return (
      <Container maxW="7xl" py={8}>
        <Box textAlign="center">
          <Text fontSize="lg" color="#191d16">
            Loading ad details...
          </Text>
        </Box>
      </Container>
    );
  }

  if (error || !campaign) {
    return (
      <Container maxW="7xl" py={8}>
        <Box textAlign="center">
          <Text fontSize="lg" color="red.500" mb={4}>
            Ad not found
          </Text>
          <Button
            onClick={navigateBackToCampaigns}
            bg="#0959ED"
            color="white"
            _hover={{ bg: "#0849D1" }}
          >
            Back to Campaigns
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="7xl" py={8}>
        {/* Breadcrumb Header - consistent with CreateLink */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Button 
                onClick={navigateBackToCampaigns}
                variant="ghost" 
                size="sm" 
                _hover={{ bg: "gray.100" }}
              >
                <Text mr={2}>←</Text>
                Back to Campaigns
              </Button>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                {campaign.campaign_name}
              </Text>
              <Text color="gray.500">/</Text>
              <Text color="gray.900" fontWeight="semibold">
                {campaign.ad_name}
              </Text>
            </HStack>
            <Badge colorScheme="green" px={3} py={1} rounded="full">
              <HStack gap={1}>
                <Text>Active Ad</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>

      {/* Campaign Information */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6} mb={8}>
        <Box
          bg="white"
          p={6}
          rounded="lg"
          shadow="md"
          border="1px"
          borderColor="gray.200"
        >
          <VStack align="start" gap={3}>
            <Heading size="md" color="#191d16">
              Campaign Info
            </Heading>
            <VStack align="start" gap={2} w="full">
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Campaign ID:
                </Text>
                <Badge colorScheme="blue">{campaign.campaign_id}</Badge>
              </HStack>
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Campaign Name:
                </Text>
                <Text fontSize="sm">{campaign.campaign_name || "N/A"}</Text>
              </HStack>
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Account ID:
                </Text>
                <Text fontSize="sm">{campaign.account_id || "N/A"}</Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>

        <Box
          bg="white"
          p={6}
          rounded="lg"
          shadow="md"
          border="1px"
          borderColor="gray.200"
        >
          <VStack align="start" gap={3}>
            <Heading size="md" color="#191d16">
              Ad Set Info
            </Heading>
            <VStack align="start" gap={2} w="full">
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Adset ID:
                </Text>
                <Badge colorScheme="green">{campaign.adset_id}</Badge>
              </HStack>
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Adset Name:
                </Text>
                <Text fontSize="sm">{campaign.adset_name || "N/A"}</Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>

        <Box
          bg="white"
          p={6}
          rounded="lg"
          shadow="md"
          border="1px"
          borderColor="gray.200"
        >
          <VStack align="start" gap={3}>
            <Heading size="md" color="#191d16">
              Ad Info
            </Heading>
            <VStack align="start" gap={2} w="full">
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Ad ID:
                </Text>
                <Badge colorScheme="purple">{campaign.ad_id}</Badge>
              </HStack>
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Ad Name:
                </Text>
                <Text fontSize="sm">{campaign.ad_name || "N/A"}</Text>
              </HStack>
              <HStack justify="space-between" w="full">
                <Text fontWeight="medium" color="gray.700">
                  Created:
                </Text>
                <Text fontSize="sm">
                  {campaign.created_time
                    ? new Date(campaign.created_time).toLocaleDateString()
                    : "N/A"}
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Performance Metrics */}
      <Box
        bg="white"
        p={6}
        rounded="lg"
        shadow="md"
        border="1px"
        borderColor="gray.200"
        mb={8}
      >
        <Heading size="md" color="#191d16" mb={6}>
          Performance Metrics
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 4, lg: 5 }} gap={6}>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Amount Spent
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="red.600">
              ${campaign.spend ? parseFloat(campaign.spend).toFixed(2) : "0.00"}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Impressions
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="purple.600">
              {campaign.impressions
                ? parseInt(campaign.impressions).toLocaleString()
                : "0"}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              CPM
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.600">
              ${campaign.cpm ? parseFloat(campaign.cpm).toFixed(2) : "0.00"}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Link CTR
            </Text>
            <Text
              fontSize="xl"
              fontWeight="bold"
              color={
                campaign.ctr && parseFloat(campaign.ctr) > 2
                  ? "green.600"
                  : campaign.ctr && parseFloat(campaign.ctr) > 1
                    ? "orange.500"
                    : "red.500"
              }
            >
              {campaign.ctr ? parseFloat(campaign.ctr).toFixed(2) : "0.00"}%
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Frequency
            </Text>
            <Text fontSize="xl" fontWeight="bold">
              {campaign.frequency
                ? parseFloat(campaign.frequency).toFixed(2)
                : "0.00"}
            </Text>
          </Box>
        </SimpleGrid>

        {/* Calendly Event Metrics */}
        <Box mt={6} pt={6} borderTop="1px" borderColor="gray.200">
          <Heading size="sm" color="#191d16" mb={4}>
            Calendly Event Metrics
          </Heading>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                # CalendlyEventScheduled
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="teal.600">
                {campaign.calendly_events || 0}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Cost per CalendlyEvent
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="teal.700">
                $
                {campaign.calendly_events && campaign.spend
                  ? (
                      parseFloat(campaign.spend) / campaign.calendly_events
                    ).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                # CalendlyComEventScheduled
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="cyan.600">
                {campaign.calendly_com_events || 0}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Cost per CalendlyComEvent
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="cyan.700">
                $
                {campaign.calendly_com_events && campaign.spend
                  ? (
                      parseFloat(campaign.spend) / campaign.calendly_com_events
                    ).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* Customer Outcomes Section */}
      <Box
        bg="white"
        p={6}
        rounded="lg"
        shadow="md"
        border="1px"
        borderColor="gray.200"
        mb={8}
      >
        <Heading size="md" color="#191d16" mb={6}>
          Customer Outcomes
        </Heading>

        {/* Outcome Counts and Costs */}
        <SimpleGrid columns={{ base: 2, md: 4 }} gap={6} mb={8}>
          {/* No-Show */}
          <Box
            bg="red.50"
            border="2px"
            borderColor="red.200"
            rounded="xl"
            p={4}
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="bold" color="red.600" mb={1}>
              3
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="red.700">
              No-Show
            </Text>
            <Text fontSize="xs" color="red.500" mt={1}>
              Missed appointments
            </Text>
            <Box mt={2} pt={2} borderTop="1px" borderColor="red.200">
              <Text fontSize="xs" color="red.600">
                Cost per No-Show
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="red.700">
                $
                {campaign.spend && 3 > 0
                  ? (parseFloat(campaign.spend) / 3).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
          </Box>

          {/* Showed Up */}
          <Box
            bg="blue.50"
            border="2px"
            borderColor="blue.200"
            rounded="xl"
            p={4}
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="bold" color="blue.600" mb={1}>
              12
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="blue.700">
              Showed Up
            </Text>
            <Text fontSize="xs" color="blue.500" mt={1}>
              Attended meetings
            </Text>
            <Box mt={2} pt={2} borderTop="1px" borderColor="blue.200">
              <Text fontSize="xs" color="blue.600">
                Cost per Showed Up
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.700">
                $
                {campaign.spend && 12 > 0
                  ? (parseFloat(campaign.spend) / 12).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
          </Box>

          {/* Sent for Pre-Approval */}
          <Box
            bg="orange.50"
            border="2px"
            borderColor="orange.200"
            rounded="xl"
            p={4}
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="bold" color="orange.600" mb={1}>
              8
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="orange.700">
              Sent for Pre-Approval
            </Text>
            <Text fontSize="xs" color="orange.500" mt={1}>
              Financing process
            </Text>
            <Box mt={2} pt={2} borderTop="1px" borderColor="orange.200">
              <Text fontSize="xs" color="orange.600">
                Cost per Pre-Approval
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="orange.700">
                $
                {campaign.spend && 8 > 0
                  ? (parseFloat(campaign.spend) / 8).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
          </Box>

          {/* Assigned to AM */}
          <Box
            bg="green.50"
            border="2px"
            borderColor="green.200"
            rounded="xl"
            p={4}
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="bold" color="green.600" mb={1}>
              5
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="green.700">
              Assigned to AM
            </Text>
            <Text fontSize="xs" color="green.500" mt={1}>
              Account management
            </Text>
            <Box mt={2} pt={2} borderTop="1px" borderColor="green.200">
              <Text fontSize="xs" color="green.600">
                Cost per AM Assignment
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="green.700">
                $
                {campaign.spend && 5 > 0
                  ? (parseFloat(campaign.spend) / 5).toFixed(2)
                  : "0.00"}
              </Text>
            </Box>
          </Box>
        </SimpleGrid>

        {/* Conversion Rates */}
        <Box bg="gray.50" p={4} rounded="lg">
          <Heading size="sm" color="#191d16" mb={4}>
            Conversion Rates
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.700">
                Show Up Rate:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.600">
                {((12 / (12 + 3)) * 100).toFixed(1)}%
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.700">
                Pre-Approval Rate:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="orange.600">
                {((8 / 12) * 100).toFixed(1)}%
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.700">
                AM Assignment Rate:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="green.600">
                {((5 / 8) * 100).toFixed(1)}%
              </Text>
            </HStack>
          </SimpleGrid>
        </Box>

        {/* Lead Engagement Metrics */}
        <Box bg="purple.50" p={4} rounded="lg" mt={4}>
          <Heading size="sm" color="#191d16" mb={4}>
            Lead Engagement Metrics
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Box>
              <Text fontSize="sm" color="gray.700" mb={1}>
                Avg Touchpoints per Converted Lead
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="purple.600">
                3.2
              </Text>
              <Text fontSize="xs" color="purple.500">
                Before conversion
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.700" mb={1}>
                Avg Lag: First Touch to Conversion
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="purple.600">
                4.5 days
              </Text>
              <Text fontSize="xs" color="purple.500">
                From first ad interaction
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Summary Stats */}
        <Box mt={6} pt={4} borderTop="1px" borderColor="gray.200">
          <HStack justify="space-between">
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.600">
                Total Customers
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                28
              </Text>
            </VStack>
            <VStack align="center" gap={1}>
              <Text fontSize="sm" color="gray.600">
                Conversion Rate
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                42.9%
              </Text>
            </VStack>
            <VStack align="end" gap={1}>
              <Text fontSize="sm" color="gray.600">
                Success Rate
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="green.600">
                17.9%
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Customer Outcome Costs */}
      <Box
        bg="white"
        p={6}
        rounded="lg"
        shadow="md"
        border="1px"
        borderColor="gray.200"
        mb={8}
      >
        <Heading size="md" color="#191d16" mb={6}>
          Customer Outcome Costs
        </Heading>

        <SimpleGrid columns={3} gap={6}>
          {/* Cost per Assigned to AM */}
          <Box
            bg="green.50"
            border="2px"
            borderColor="green.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="green.600" mb={2}>
              $
              {campaign.spend && campaign.spend !== "0"
                ? (parseFloat(campaign.spend) / 5).toFixed(2)
                : "0.00"}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="green.700" mb={1}>
              Cost per Assigned to AM
            </Text>
            <Text fontSize="sm" color="green.500">
              Total spend ÷ 5 assignments
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="green.200">
              <HStack justify="center" gap={2}>
                <Text fontSize="xs" color="green.400">
                  5 customers assigned
                </Text>
                <Text fontSize="xs" color="green.600">
                  •
                </Text>
                <Text fontSize="xs" color="green.400">
                  $
                  {campaign.spend
                    ? parseFloat(campaign.spend).toFixed(2)
                    : "0.00"}{" "}
                  total
                </Text>
              </HStack>
            </Box>
          </Box>

          {/* Cost per Sent for Pre-Approval */}
          <Box
            bg="orange.50"
            border="2px"
            borderColor="orange.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="orange.600" mb={2}>
              $
              {campaign.spend && campaign.spend !== "0"
                ? (parseFloat(campaign.spend) / 8).toFixed(2)
                : "0.00"}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="orange.700" mb={1}>
              Cost per Sent for Pre-Approval
            </Text>
            <Text fontSize="sm" color="orange.500">
              Total spend ÷ 8 pre-approvals
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="orange.200">
              <HStack justify="center" gap={2}>
                <Text fontSize="xs" color="orange.400">
                  8 sent for pre-approval
                </Text>
                <Text fontSize="xs" color="orange.600">
                  •
                </Text>
                <Text fontSize="xs" color="orange.400">
                  $
                  {campaign.spend
                    ? parseFloat(campaign.spend).toFixed(2)
                    : "0.00"}{" "}
                  total
                </Text>
              </HStack>
            </Box>
          </Box>

          {/* Cost per Showed Up */}
          <Box
            bg="blue.50"
            border="2px"
            borderColor="blue.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="blue.600" mb={2}>
              $
              {campaign.spend && campaign.spend !== "0"
                ? (parseFloat(campaign.spend) / 12).toFixed(2)
                : "0.00"}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="blue.700" mb={1}>
              Cost per Showed Up
            </Text>
            <Text fontSize="sm" color="blue.500">
              Total spend ÷ 12 attendees
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="blue.200">
              <HStack justify="center" gap={2}>
                <Text fontSize="xs" color="blue.400">
                  12 customers showed up
                </Text>
                <Text fontSize="xs" color="blue.600">
                  •
                </Text>
                <Text fontSize="xs" color="blue.400">
                  $
                  {campaign.spend
                    ? parseFloat(campaign.spend).toFixed(2)
                    : "0.00"}{" "}
                  total
                </Text>
              </HStack>
            </Box>
          </Box>
        </SimpleGrid>

        {/* Cost Efficiency Summary */}
        <Box mt={6} pt={4} borderTop="1px" borderColor="gray.200">
          <Text fontSize="sm" color="gray.600" mb={3} textAlign="center">
            Cost Efficiency Analysis
          </Text>
          <HStack justify="space-between">
            <VStack align="start" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Most Efficient
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="green.600">
                Assigned to AM
              </Text>
              <Text fontSize="xs" color="green.500">
                Highest value outcome
              </Text>
            </VStack>
            <VStack align="center" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Average Cost per Outcome
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                $
                {campaign.spend && campaign.spend !== "0"
                  ? (
                      (parseFloat(campaign.spend) / 5 +
                        parseFloat(campaign.spend) / 8 +
                        parseFloat(campaign.spend) / 12) /
                      3
                    ).toFixed(2)
                  : "0.00"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Across all outcomes
              </Text>
            </VStack>
            <VStack align="end" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Volume Leader
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.600">
                Showed Up
              </Text>
              <Text fontSize="xs" color="blue.500">
                Highest attendance rate
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Revenue Generated Section */}
      <Box
        bg="white"
        p={6}
        rounded="lg"
        shadow="md"
        border="1px"
        borderColor="gray.200"
        mb={8}
      >
        <Heading size="md" color="#191d16" mb={6}>
          Return on Ad Spend (ROAS)
        </Heading>

        <SimpleGrid columns={3} gap={6}>
          {/* ROAS for Assigned to AM */}
          <Box
            bg="green.50"
            border="2px"
            borderColor="green.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="green.600" mb={2}>
              {(() => {
                const assignedCustomers = mockCustomers.filter(
                  (c) =>
                    c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                );
                const totalCommission = assignedCustomers.reduce(
                  (sum, customer) => {
                    return sum + (customer.zownContribution || 0);
                  },
                  0
                );
                const spend = campaign.spend
                  ? parseFloat(campaign.spend) * 100
                  : 0;

                if (spend === 0) return "∞";
                const roas = totalCommission / spend;
                return `${roas.toFixed(1)}x`;
              })()}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="green.700" mb={1}>
              ROAS: Assigned to AM
            </Text>
            <Text fontSize="sm" color="green.500">
              Commission return per $1 spent
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="green.200">
              <VStack gap={1}>
                <HStack justify="center" gap={2}>
                  <Text fontSize="xs" color="green.400">
                    {(() => {
                      const assignedCustomers = mockCustomers.filter(
                        (c) =>
                          c.stage === "CONGRATULATIONS" ||
                          c.stage === "COMPLETED"
                      );
                      const totalCommission = assignedCustomers.reduce(
                        (sum, customer) => {
                          return sum + (customer.zownContribution || 0);
                        },
                        0
                      );
                      return formatMoney(totalCommission);
                    })()}{" "}
                    commission
                  </Text>
                  <Text fontSize="xs" color="green.600">
                    •
                  </Text>
                  <Text fontSize="xs" color="green.400">
                    {
                      mockCustomers.filter(
                        (c) =>
                          c.stage === "CONGRATULATIONS" ||
                          c.stage === "COMPLETED"
                      ).length
                    }{" "}
                    customers
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {(() => {
                    const assignedCustomers = mockCustomers.filter(
                      (c) =>
                        c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                    );
                    const totalRevenue = assignedCustomers.reduce(
                      (sum, customer) => {
                        return sum + (customer.affordability || 0);
                      },
                      0
                    );
                    return formatMoney(totalRevenue);
                  })()}{" "}
                  total home value
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Projected ROAS from Pipeline */}
          <Box
            bg="orange.50"
            border="2px"
            borderColor="orange.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="orange.600" mb={2}>
              {(() => {
                const preApprovalCustomers = mockCustomers.filter(
                  (c) =>
                    c.qualificationStatus === "QUALIFIED" &&
                    c.stage !== "CONGRATULATIONS" &&
                    c.stage !== "COMPLETED"
                );
                const projectedCommission = preApprovalCustomers.reduce(
                  (sum, customer) => {
                    return sum + (customer.zownContribution || 0);
                  },
                  0
                );
                const spend = campaign.spend
                  ? parseFloat(campaign.spend) * 100
                  : 0;

                if (spend === 0) return "∞";
                const projectedRoas = projectedCommission / spend;
                return `${projectedRoas.toFixed(1)}x`;
              })()}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="orange.700" mb={1}>
              Projected ROAS: Pipeline
            </Text>
            <Text fontSize="sm" color="orange.500">
              Potential return if all close
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="orange.200">
              <VStack gap={1}>
                <HStack justify="center" gap={2}>
                  <Text fontSize="xs" color="orange.400">
                    {(() => {
                      const preApprovalCustomers = mockCustomers.filter(
                        (c) =>
                          c.qualificationStatus === "QUALIFIED" &&
                          c.stage !== "CONGRATULATIONS" &&
                          c.stage !== "COMPLETED"
                      );
                      const projectedCommission = preApprovalCustomers.reduce(
                        (sum, customer) => {
                          return sum + (customer.zownContribution || 0);
                        },
                        0
                      );
                      return formatMoney(projectedCommission);
                    })()}{" "}
                    potential commission
                  </Text>
                  <Text fontSize="xs" color="orange.600">
                    •
                  </Text>
                  <Text fontSize="xs" color="orange.400">
                    {
                      mockCustomers.filter(
                        (c) =>
                          c.qualificationStatus === "QUALIFIED" &&
                          c.stage !== "CONGRATULATIONS" &&
                          c.stage !== "COMPLETED"
                      ).length
                    }{" "}
                    qualified
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {(() => {
                    const preApprovalCustomers = mockCustomers.filter(
                      (c) =>
                        c.qualificationStatus === "QUALIFIED" &&
                        c.stage !== "CONGRATULATIONS" &&
                        c.stage !== "COMPLETED"
                    );
                    const projectedRevenue = preApprovalCustomers.reduce(
                      (sum, customer) => {
                        return sum + (customer.affordability || 0);
                      },
                      0
                    );
                    return formatMoney(projectedRevenue);
                  })()}{" "}
                  potential home value
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Overall Campaign ROAS */}
          <Box
            bg="blue.50"
            border="2px"
            borderColor="blue.200"
            rounded="xl"
            p={6}
            textAlign="center"
          >
            <Text fontSize="3xl" fontWeight="bold" color="blue.600" mb={2}>
              {(() => {
                // Calculate total commission from successful customers
                const currentCommission = mockCustomers
                  .filter(
                    (c) =>
                      c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                  )
                  .reduce(
                    (sum, customer) => sum + (customer.zownContribution || 0),
                    0
                  );

                const spend = campaign.spend
                  ? parseFloat(campaign.spend) * 100
                  : 0;

                if (spend === 0) return "∞";
                const roas = currentCommission / spend;
                return `${roas.toFixed(1)}x`;
              })()}
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="blue.700" mb={1}>
              Overall Campaign ROAS
            </Text>
            <Text fontSize="sm" color="blue.500">
              Current return on ad spend
            </Text>
            <Box mt={3} pt={3} borderTop="1px" borderColor="blue.200">
              <VStack gap={1}>
                <HStack justify="center" gap={2}>
                  <Text fontSize="xs" color="blue.400">
                    {(() => {
                      const currentCommission = mockCustomers
                        .filter(
                          (c) =>
                            c.stage === "CONGRATULATIONS" ||
                            c.stage === "COMPLETED"
                        )
                        .reduce(
                          (sum, customer) =>
                            sum + (customer.zownContribution || 0),
                          0
                        );
                      return formatMoney(currentCommission);
                    })()}{" "}
                    earned
                  </Text>
                  <Text fontSize="xs" color="blue.600">
                    •
                  </Text>
                  <Text fontSize="xs" color="blue.400">
                    $
                    {campaign.spend
                      ? parseFloat(campaign.spend).toFixed(2)
                      : "0.00"}{" "}
                    spent
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {
                    mockCustomers.filter(
                      (c) => c.qualificationStatus === "QUALIFIED"
                    ).length
                  }{" "}
                  total qualified customers
                </Text>
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>

        {/* ROAS Insights & Optimization */}
        <Box mt={6} pt={4} borderTop="1px" borderColor="gray.200">
          <Text fontSize="sm" color="gray.600" mb={3} textAlign="center">
            ROAS Performance & Optimization Insights
          </Text>
          <HStack justify="space-between">
            <VStack align="start" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Break-Even ROAS
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="green.600">
                1.0x
              </Text>
              <Text fontSize="xs" color="green.500">
                Profitable above this
              </Text>
            </VStack>
            <VStack align="center" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Target ROAS
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                4.0x
              </Text>
              <Text fontSize="xs" color="gray.500">
                Industry benchmark
              </Text>
            </VStack>
            <VStack align="end" gap={1}>
              <Text fontSize="xs" color="gray.500">
                Performance Status
              </Text>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={(() => {
                  const currentCommission = mockCustomers
                    .filter(
                      (c) =>
                        c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                    )
                    .reduce(
                      (sum, customer) => sum + (customer.zownContribution || 0),
                      0
                    );
                  const spend = campaign.spend
                    ? parseFloat(campaign.spend) * 100
                    : 0;
                  const roas = spend > 0 ? currentCommission / spend : 0;

                  return roas >= 4
                    ? "green.600"
                    : roas >= 1
                      ? "orange.600"
                      : "red.600";
                })()}
              >
                {(() => {
                  const currentCommission = mockCustomers
                    .filter(
                      (c) =>
                        c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                    )
                    .reduce(
                      (sum, customer) => sum + (customer.zownContribution || 0),
                      0
                    );
                  const spend = campaign.spend
                    ? parseFloat(campaign.spend) * 100
                    : 0;
                  const roas = spend > 0 ? currentCommission / spend : 0;

                  if (roas >= 4) return "Excellent";
                  if (roas >= 1) return "Profitable";
                  return "Needs Optimization";
                })()}
              </Text>
              <Text fontSize="xs" color="blue.500">
                Campaign health
              </Text>
            </VStack>
          </HStack>

          {/* Action Recommendations */}
          <Box mt={4} p={3} bg="gray.50" rounded="md">
            <Text fontSize="xs" color="gray.600" mb={2} fontWeight="semibold">
              💡 Optimization Recommendations:
            </Text>
            <Text fontSize="xs" color="gray.600">
              {(() => {
                const currentCommission = mockCustomers
                  .filter(
                    (c) =>
                      c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                  )
                  .reduce(
                    (sum, customer) => sum + (customer.zownContribution || 0),
                    0
                  );
                const spend = campaign.spend
                  ? parseFloat(campaign.spend) * 100
                  : 0;
                const roas = spend > 0 ? currentCommission / spend : 0;
                const qualifiedCount = mockCustomers.filter(
                  (c) => c.qualificationStatus === "QUALIFIED"
                ).length;
                const closedCount = mockCustomers.filter(
                  (c) =>
                    c.stage === "CONGRATULATIONS" || c.stage === "COMPLETED"
                ).length;

                if (roas >= 4) {
                  return "🚀 Campaign performing excellently! Consider increasing budget to scale successful performance.";
                } else if (roas >= 1) {
                  return `📈 Campaign is profitable. Focus on improving conversion from qualified (${qualifiedCount}) to closed (${closedCount}) to boost ROAS.`;
                } else {
                  return "⚠️ ROAS below break-even. Consider optimizing targeting, creative, or landing page to improve conversion quality.";
                }
              })()}
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Customers Section */}
      {mockCustomers.length === 0 ? (
        <Box p={12} textAlign="center" bg="white" rounded="lg" shadow="md">
          <Heading size="lg" color="#191d16" mb={4}>
            No customers found
          </Heading>
          <Text color="gray.600">
            No customers are associated with this ad.
          </Text>
        </Box>
      ) : (
        <Box
          bg="white"
          rounded="lg"
          shadow="md"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
          mb={8}
        >
          <Box p={4} borderBottom="1px" borderColor="gray.200">
            <Heading size="md" color="#191d16">
              Customers ({mockCustomers.length})
            </Heading>
          </Box>
          <Box height="500px" width="100%">
            <AgGridReact
              rowData={mockCustomers}
              columnDefs={customerColumnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowClicked={(event) => handleCustomerRowClick(event.data)}
              onFirstDataRendered={(params) => {
                // Auto-size columns when data is first rendered
                params.api.autoSizeAllColumns();
              }}
              onGridSizeChanged={(params) => {
                // Auto-size columns when grid is resized
                params.api.autoSizeAllColumns();
              }}
              animateRows={true}
              cellSelection={true}
              enableCharts={true}
              sideBar={{
                toolPanels: [
                  {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel"
                  },
                  {
                    id: "filters",
                    labelDefault: "Filters",
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel"
                  }
                ],
                hiddenByDefault: true
              }}
              rowSelection={{
                mode: "singleRow",
                enableClickSelection: true
              }}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50]}
              paginationAutoPageSize={false}
              suppressPaginationPanel={false}
              paginationNumberFormatter={(params) => {
                return params.value.toLocaleString();
              }}
              localeText={{
                page: "Page",
                more: "More",
                to: "to",
                of: "of",
                next: "Next",
                last: "Last",
                first: "First",
                previous: "Previous",
                loadingOoo: "Loading...",
                noRowsToShow: "No customers to show"
              }}
              rowHeight={50}
              loading={false}
            />
          </Box>
        </Box>
      )}

      {/* URL Parameters Display */}
      <Box mt={8} p={4} bg="gray.50" rounded="lg">
        <Text fontSize="sm" color="gray.600" mb={2}>
          Current URL Parameters:
        </Text>
        <Text fontSize="sm" fontFamily="mono" color="gray.800">
          Campaign ID: {campaignId} | Adset ID: {adsetId} | Ad ID: {adId}
        </Text>
      </Box>

      {/* Customer Journey Modal Overlay */}
      <CustomerDetailModel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customer={selectedCustomer}
      />
      </Container>
    </Box>
  );
}

export default AdDetail;
